// src/components/onboarding/OnboardingSystem.jsx
// ✅ Tour interattivo + Modal benvenuto + Checklist onboarding
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    XMarkIcon, ArrowRightIcon, ArrowLeftIcon,
    CheckCircleIcon, SparklesIcon, RocketLaunchIcon,
    FolderIcon, CurrencyDollarIcon, UserGroupIcon,
    ChartBarIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const STORAGE_KEY = 'infrapilot_onboarding';

// ==================== TOUR STEPS ====================
const TOUR_STEPS = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        description: 'Qui hai una visione completa di tutti i tuoi cantieri — budget, spese, avanzamento lavori e alert in tempo reale.',
        target: '[data-tour="dashboard"]',
        position: 'right',
        icon: '🏠',
    },
    {
        id: 'projects',
        title: 'Gestione Progetti',
        description: 'Crea e gestisci i tuoi cantieri. Ogni progetto ha budget, fasi, attività e controllo costi integrato.',
        target: '[data-tour="projects"]',
        position: 'right',
        icon: '🏗️',
    },
    {
        id: 'expenses',
        title: 'Controllo Spese',
        description: 'Registra ogni spesa in tempo reale — materiali, manodopera, attrezzature. Valida le spese e monitora il budget.',
        target: '[data-tour="expenses"]',
        position: 'right',
        icon: '💰',
    },
    {
        id: 'reports',
        title: 'Report e Analytics',
        description: 'Genera report dettagliati per ogni progetto. Esporta in PDF per clienti e stakeholder.',
        target: '[data-tour="reports"]',
        position: 'right',
        icon: '📊',
    },
    {
        id: 'ai',
        title: 'AI Assistant',
        description: 'Il tuo assistente AI analizza i tuoi cantieri, suggerisce ottimizzazioni del budget e identifica rischi.',
        target: '[data-tour="ai-chat"]',
        position: 'right',
        icon: '✨',
    },
    {
        id: 'new-project',
        title: 'Crea il tuo primo progetto',
        description: 'Clicca qui per creare il tuo primo cantiere. Inserisci budget, date e il gioco è fatto!',
        target: '[data-tour="new-project"]',
        position: 'bottom',
        icon: '🚀',
        action: { label: 'Crea progetto', path: '/projects/new' },
    },
];

// ==================== CHECKLIST ====================
const CHECKLIST_ITEMS = [
    { id: 'profile', label: 'Completa il tuo profilo', icon: '👤', path: '/profile', points: 10 },
    { id: 'project', label: 'Crea il primo progetto', icon: '🏗️', path: '/projects/new', points: 20 },
    { id: 'expense', label: 'Aggiungi una spesa', icon: '💰', path: '/expenses/new', points: 15 },
    { id: 'team', label: 'Invita un membro del team', icon: '👥', path: '/settings?tab=team', points: 15 },
    { id: 'report', label: 'Genera il primo report', icon: '📊', path: '/reports', points: 10 },
    { id: 'ai', label: 'Prova l\'AI Assistant', icon: '✨', path: '/ai-chat', points: 30 },
];

// ==================== TOOLTIP POSIZIONAMENTO ====================
const getTooltipStyle = (rect, position) => {
    if (!rect) return {};
    const offset = 16;
    switch (position) {
        case 'right': return { top: rect.top + rect.height / 2, left: rect.right + offset };
        case 'left': return { top: rect.top + rect.height / 2, left: rect.left - offset - 300 };
        case 'bottom': return { top: rect.bottom + offset, left: rect.left + rect.width / 2 };
        case 'top': return { top: rect.top - offset - 180, left: rect.left + rect.width / 2 };
        default: return { top: rect.top, left: rect.right + offset };
    }
};

// ==================== TOUR TOOLTIP ====================
const TourTooltip = ({ step, stepIndex, totalSteps, onNext, onPrev, onSkip, onAction, navigate }) => {
    const [rect, setRect] = useState(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = document.querySelector(step.target);
        if (el) {
            const r = el.getBoundingClientRect();
            setRect(r);
            el.style.position = 'relative';
            el.style.zIndex = '1001';
            setTimeout(() => setVisible(true), 100);
        }
        return () => {
            if (el) { el.style.position = ''; el.style.zIndex = ''; }
        };
    }, [step]);

    const style = getTooltipStyle(rect, step.position);
    const isLast = stepIndex === totalSteps - 1;

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/50 z-[1000]" onClick={onSkip} />

            {/* Highlight dell'elemento */}
            {rect && (
                <div className="fixed z-[1001] pointer-events-none rounded-xl ring-4 ring-blue-400 ring-offset-2 transition-all"
                    style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }} />
            )}

            {/* Tooltip */}
            <div className={`fixed z-[1002] w-72 bg-white rounded-2xl shadow-2xl p-5 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                style={{ top: style.top, left: style.left, transform: step.position === 'bottom' ? 'translateX(-50%)' : 'translateY(-50%)' }}>

                {/* Arrow */}
                {step.position === 'right' && (
                    <div className="absolute left-0 top-1/2 -translate-x-2 -translate-y-1/2 w-3 h-3 bg-white rotate-45 shadow-sm" />
                )}

                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl">{step.icon}</span>
                        <h3 className="font-bold text-gray-900 text-sm">{step.title}</h3>
                    </div>
                    <button onClick={onSkip} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <p className="text-sm text-gray-600 mb-4 leading-relaxed">{step.description}</p>

                {/* Progress dots */}
                <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div key={i} className={`h-1.5 rounded-full transition-all ${i === stepIndex ? 'w-6 bg-blue-500' : 'w-1.5 bg-gray-200'}`} />
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    {stepIndex > 0 && (
                        <button onClick={onPrev}
                            className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                            <ArrowLeftIcon className="w-3 h-3" /> Indietro
                        </button>
                    )}
                    {step.action && (
                        <button onClick={() => { onAction(); navigate(step.action.path); }}
                            className="flex-1 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700">
                            {step.action.label}
                        </button>
                    )}
                    <button onClick={onNext}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                        {isLast ? 'Fine!' : 'Avanti'} <ArrowRightIcon className="w-3 h-3" />
                    </button>
                </div>

                <button onClick={onSkip} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-2">
                    Salta il tour
                </button>
            </div>
        </>
    );
};

// ==================== WELCOME MODAL ====================
const WelcomeModal = ({ user, onStartTour, onSkip }) => {
    const [visible, setVisible] = useState(false);
    useEffect(() => { setTimeout(() => setVisible(true), 300); }, []);

    const features = [
        { icon: FolderIcon, label: 'Gestione Cantieri', desc: 'Progetti, fasi, attività' },
        { icon: CurrencyDollarIcon, label: 'Controllo Spese', desc: 'Budget in tempo reale' },
        { icon: SparklesIcon, label: 'AI Assistant', desc: 'Analisi e suggerimenti' },
        { icon: UserGroupIcon, label: 'Team Collaborativo', desc: 'Ruoli e permessi custom' },
        { icon: ChartBarIcon, label: 'Report Automatici', desc: 'PDF per clienti' },
        { icon: ShieldCheckIcon, label: 'Multi-valuta', desc: 'EUR, GNF, XOF e più' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className={`relative bg-white rounded-3xl shadow-2xl w-full max-w-lg transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>

                {/* Header */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-t-3xl p-8 text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <RocketLaunchIcon className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-1">
                        Benvenuto su InfraPilot! 🎉
                    </h1>
                    <p className="text-blue-200 text-sm">
                        Ciao {user?.firstName}! Gestisci i tuoi cantieri in modo intelligente.
                    </p>
                </div>

                {/* Features grid */}
                <div className="p-6">
                    <p className="text-sm text-gray-500 text-center mb-4">Cosa puoi fare con InfraPilot:</p>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {features.map((f, i) => (
                            <div key={i} className="text-center p-3 bg-gray-50 rounded-xl">
                                <f.icon className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                                <p className="text-xs font-semibold text-gray-900">{f.label}</p>
                                <p className="text-xs text-gray-400">{f.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="space-y-2">
                        <button onClick={onStartTour}
                            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition flex items-center justify-center gap-2">
                            <RocketLaunchIcon className="w-4 h-4" />
                            Inizia il tour guidato (2 min)
                        </button>
                        <button onClick={onSkip}
                            className="w-full py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50 transition">
                            Esploro da solo
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== CHECKLIST WIDGET ====================
const ChecklistWidget = ({ completed, onComplete, onClose }) => {
    const navigate = useNavigate();
    const total = CHECKLIST_ITEMS.length;
    const doneCount = completed.length;
    const pct = Math.round((doneCount / total) * 100);
    const totalPoints = CHECKLIST_ITEMS.filter(i => completed.includes(i.id)).reduce((s, i) => s + i.points, 0);
    const maxPoints = CHECKLIST_ITEMS.reduce((s, i) => s + i.points, 0);

    if (doneCount === total) return null;

    return (
        <div className="fixed bottom-6 right-6 z-40 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                <div>
                    <p className="text-white font-semibold text-sm">Inizia con InfraPilot</p>
                    <p className="text-blue-200 text-xs">{doneCount}/{total} completati · {totalPoints}/{maxPoints} punti</p>
                </div>
                <button onClick={onClose} className="text-blue-200 hover:text-white">
                    <XMarkIcon className="w-4 h-4" />
                </button>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-gray-100">
                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>

            <div className="p-3 space-y-1.5 max-h-64 overflow-y-auto">
                {CHECKLIST_ITEMS.map(item => {
                    const done = completed.includes(item.id);
                    return (
                        <button key={item.id} onClick={() => { if (!done) { onComplete(item.id); navigate(item.path); } }}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all ${done ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                            <span className="text-lg flex-shrink-0">{item.icon}</span>
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium truncate ${done ? 'text-emerald-600 line-through' : 'text-gray-900'}`}>
                                    {item.label}
                                </p>
                                <p className="text-xs text-gray-400">+{item.points} punti</p>
                            </div>
                            {done
                                ? <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                : <ArrowRightIcon className="w-3 h-3 text-gray-300 flex-shrink-0" />
                            }
                        </button>
                    );
                })}
            </div>

            {pct > 0 && pct < 100 && (
                <div className="px-3 pb-3">
                    <p className="text-xs text-center text-gray-400">{pct}% completato — continua così! 💪</p>
                </div>
            )}
        </div>
    );
};

// ==================== MAIN ONBOARDING SYSTEM ====================
const OnboardingSystem = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [state, setState] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : {
                welcomeSeen: false,
                tourCompleted: false,
                checklistCompleted: [],
                checklistDismissed: false,
            };
        } catch { return { welcomeSeen: false, tourCompleted: false, checklistCompleted: [], checklistDismissed: false }; }
    });

    const [tourStep, setTourStep] = useState(0);
    const [showWelcome, setShowWelcome] = useState(false);
    const [showTour, setShowTour] = useState(false);
    const [showChecklist, setShowChecklist] = useState(false);

    const save = useCallback((updates) => {
        const next = { ...state, ...updates };
        setState(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }, [state]);

    // Mostra welcome modal al primo accesso
    useEffect(() => {
        if (user && !state.welcomeSeen) {
            setTimeout(() => setShowWelcome(true), 1000);
        }
        if (user && state.welcomeSeen && !state.tourCompleted && !state.checklistDismissed) {
            setTimeout(() => setShowChecklist(true), 500);
        }
    }, [user]);

    const handleStartTour = () => {
        setShowWelcome(false);
        save({ welcomeSeen: true });
        setTimeout(() => { setShowTour(true); setTourStep(0); }, 300);
    };

    const handleSkipWelcome = () => {
        setShowWelcome(false);
        save({ welcomeSeen: true });
        setTimeout(() => setShowChecklist(true), 300);
    };

    const handleTourNext = () => {
        if (tourStep < TOUR_STEPS.length - 1) {
            setTourStep(t => t + 1);
        } else {
            setShowTour(false);
            save({ tourCompleted: true });
            setTimeout(() => setShowChecklist(true), 300);
        }
    };

    const handleTourPrev = () => {
        if (tourStep > 0) setTourStep(t => t - 1);
    };

    const handleSkipTour = () => {
        setShowTour(false);
        save({ tourCompleted: true });
        setTimeout(() => setShowChecklist(true), 300);
    };

    const handleCompleteChecklist = (id) => {
        const next = [...new Set([...state.checklistCompleted, id])];
        save({ checklistCompleted: next });
    };

    const handleDismissChecklist = () => {
        setShowChecklist(false);
        save({ checklistDismissed: true });
    };

    if (!user) return null;

    return (
        <>
            {showWelcome && (
                <WelcomeModal
                    user={user}
                    onStartTour={handleStartTour}
                    onSkip={handleSkipWelcome}
                />
            )}

            {showTour && TOUR_STEPS[tourStep] && (
                <TourTooltip
                    step={TOUR_STEPS[tourStep]}
                    stepIndex={tourStep}
                    totalSteps={TOUR_STEPS.length}
                    onNext={handleTourNext}
                    onPrev={handleTourPrev}
                    onSkip={handleSkipTour}
                    onAction={() => save({ tourCompleted: true })}
                    navigate={navigate}
                />
            )}

            {showChecklist && !state.checklistDismissed && (
                <ChecklistWidget
                    completed={state.checklistCompleted}
                    onComplete={handleCompleteChecklist}
                    onClose={handleDismissChecklist}
                />
            )}
        </>
    );
};

export default OnboardingSystem;
export { CHECKLIST_ITEMS };